import { debounce } from "@solid-primitives/scheduled";
import {
  Accessor,
  Match,
  Setter,
  Show,
  Switch,
  createEffect,
  createSignal,
  onMount,
} from "solid-js";
import { CloseIcon, MagnifyingGlass } from "~/assets/svg_icons/svg_icons";

export function SearchBar(props: {
  onInput: (e: Event) => void;
  onClear: () => void;
  searchQuery: Accessor<string>;
  setSearchQuery: Setter<string>;
  isSearchActive: Accessor<boolean>;
  setIsSearchActive: Setter<boolean>;
  isProcurment?: boolean;
  title?: string;
}) {
  function onClear() {
    props.onClear();
    props.setIsSearchActive(true);
    setShowSearchBar(true);
    setOnClearCalled(true);
    searchInput?.focus();
  }

  const [showSearchBar, setShowSearchBar] = createSignal(false);
  const [onClearCalled, setOnClearCalled] = createSignal(false);

  const inputDebounce = debounce((e: Event) => {
    const inputElement = e.target as HTMLInputElement;
    props.onInput(e);
  }, 300);

  let searchInput: HTMLInputElement | undefined;
  createEffect(async () => {
    await new Promise((resolve) => setTimeout(resolve, 100));
    if (props.isSearchActive()) {
      setShowSearchBar(true);
      searchInput?.focus();
    }
  });

  return (
    <div>
      <Show when={props.isProcurment} fallback={searchBar()}>
        {procurementSearchBar()}
      </Show>
    </div>
  );

  function procurementSearchBar() {
    return (
      <div
        class={`flex h-[37px] w-[300px] items-center justify-start rounded-[8px] border border-gray-200 bg-baseTertiaryMedium px-[11px] py-2 focus-within:border-basePrimaryDark
        ${props.isProcurment ? "!sm:w-[300px] !w-full" : ""}
          `}
        onClick={() => {
          setShowSearchBar(true);
          searchInput?.focus();
        }}
      >
        <MagnifyingGlass class="text-textNormal" />
        <Show
          when={showSearchBar()}
          fallback={<div class="text-medium text-textNormal">Search</div>}
        >
          {searchInputfield({
            class:
              "w-full border-none text-textDark text-f16Bold sm:-translate-x-0 -translate-x-8  scale-75 sm:scale-100 sm:text-mediumBold bg-baseTertiaryMedium   outline-none placeholder:text-f16 sm:placeholder:text-medium placeholder:text-textNormal",
            placeholder: "Search",
            autofocus: false,
          })}
        </Show>

        <Show when={props.searchQuery().length > 0}>
          <div onClick={onClear} class="h-4 cursor-pointer">
            <CloseIcon class="cursor-pointer" width={16} height={16} />
          </div>
        </Show>
      </div>
    );
  }

  function searchBar() {
    return (
      <div
        class="  flex h-[45px] flex-row items-center justify-center rounded-xl border border-baseTertiaryDark px-5 py-3 text-gray-900"
        classList={{
          "darkTheme:bg-baseTertiaryDark": showSearchBar(),
        }}
        style={{ "box-shadow": "0px 2px 11px 0px rgba(0, 0, 0, 0.05)" }}
        onClick={() => {
          setShowSearchBar(true);
          searchInput?.focus();
        }}
      >
        <MagnifyingGlass class="text-textNormal" />
        <Switch>
          <Match when={showSearchBar()}>
            {searchInputfield({
              class:
                "ml-3 w-full border-none bg-baseTertiaryLight darkTheme:bg-baseTertiaryDark  text-f16Bold text-basePrimaryDark  outline-none placeholder:font-normal placeholder:text-textNormal",
              placeholder: props.title ?? "Search for brands or categories",
              autofocus: true,
            })}
          </Match>
          <Match when={true}>
            <div class=" ml-3 w-full text-normal  text-textNormal sm:w-auto">
              {props.title ?? "Search for brands or categories"}
            </div>
          </Match>
        </Switch>

        <Show when={props.searchQuery().length > 0}>
          <button onClick={onClear}>
            <CloseIcon class="align-text-bottom darkTheme:text-textNormal" />
          </button>
        </Show>
      </div>
    );
  }

  function searchInputfield(searchInputfieldProps: {
    class: string;
    placeholder: string;
    autofocus: boolean;
  }) {
    return (
      <input
        ref={searchInput}
        class={searchInputfieldProps.class}
        placeholder={searchInputfieldProps.placeholder}
        required
        onInput={inputDebounce}
        onFocus={(e) => {
          if (!props.isSearchActive()) {
            props.setIsSearchActive(true);
          }
        }}
        value={props.searchQuery()}
        autocomplete="off"
        autofocus={searchInputfieldProps.autofocus}
        onblur={async (e) => {
          await new Promise((resolve) => setTimeout(resolve, 250));
          if (onClearCalled()) {
            setOnClearCalled(false);
          } else {
            props.setIsSearchActive(false);
            if (props.searchQuery().length == 0) {
              setShowSearchBar(false);
            }
          }
        }}
      />
    );
  }
}
